/**
 * CoachChat — Chat panel with the tarot AI professor.
 * Displays advice, explanations, and free-form conversation.
 *
 * Messages state is owned by the parent so it persists across tab switches.
 */

import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import type { Socket } from 'socket.io-client';
import Markdown from 'react-markdown';
import { substitutePlayerNames, getCardName } from '../utils/cards';

export interface MLDeductionItem {
  id: string;
  description: string;
  probability: number;
  category: string;
  confidenceLabel?: string;
  confidence_label?: string;
  priority?: number;
}

export interface CertainDeductionItem {
  id: string;
  type: string;
  description: string;
  target_player_offset?: number | null;
  target_card_id?: number | null;
  target_suit?: number | null;
  priority: number;
  extra?: Record<string, unknown>;
}

export interface StatisticalPriorItem {
  id: string;
  category: string;
  description: string;
  probability: number;
  sampleSize: number;
  contract: number;
  priority: number;
}

export interface CoachMessage {
  id: string;
  type: 'advice' | 'chat' | 'explain' | 'user' | 'deduction_update';
  content: string;
  card?: number;
  cardName?: string;
  confidence?: number;
  entropy?: number;
  signals?: string[];
  deductions?: string[];
  mlDeductions?: MLDeductionItem[];
  safeDeductions?: MLDeductionItem[];
  certainDeductions?: CertainDeductionItem[];
  statisticalPriors?: StatisticalPriorItem[];
  alternatives?: Array<{ card: number; cardName: string; confidence: number }>;
  timestamp: number;
}

interface CoachChatProps {
  socket: Socket | null;
  isPlaying: boolean;
  currentSeat: number;
  canAskAdvice?: boolean;
  messages: CoachMessage[];
  onAddMessage: (msg: Omit<CoachMessage, 'id' | 'timestamp'>) => void;
  adviceDetailed?: boolean;
  debugML?: boolean;
  taker?: number | null;
  viewPosition?: number;
}

export default function CoachChat({ socket, isPlaying, currentSeat, canAskAdvice, messages, onAddMessage, adviceDetailed = true, debugML = false, taker = null, viewPosition = 0 }: CoachChatProps) {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const sub = useCallback((text: string) =>
    substitutePlayerNames(text, taker ?? null, currentSeat, viewPosition),
  [taker, currentSeat, viewPosition]);

  const processedMessages = useMemo(() =>
    messages.map(msg => {
      if (msg.type === 'user') return msg;
      return {
        ...msg,
        content: sub(msg.content),
        signals: msg.signals?.map(s => sub(s)),
        deductions: msg.deductions?.map(d => sub(d)),
      };
    }),
  [messages, sub]);

  useEffect(() => {
    if (!socket) return;

    const handler = (data: any) => {
      setLoading(false);

      if (data.error) {
        onAddMessage({ type: data.type ?? 'chat', content: data.error });
        return;
      }

      switch (data.type) {
        case 'advice':
          onAddMessage({
            type: 'advice',
            content: data.explanation ?? 'Pas de conseil disponible.',
            card: data.card,
            cardName: data.cardName,
            confidence: data.confidence,
            entropy: data.entropy,
            signals: data.signals,
            deductions: data.deductions,
            mlDeductions: data.mlDeductions,
            safeDeductions: data.safeDeductions,
            certainDeductions: data.certainDeductions,
            statisticalPriors: data.statisticalPriors,
            alternatives: data.alternatives,
          });
          break;
        case 'chat':
          onAddMessage({ type: 'chat', content: data.message ?? '' });
          break;
        case 'explain':
          onAddMessage({ type: 'explain', content: data.explanation ?? '' });
          break;
      }
    };

    const deductionHandler = (data: any) => {
      const lines: string[] = [];

      // Build contextual title: [Pli N] Joueur joue le X de Y
      const trickNum = data.trickNumber ?? '?';
      const playerLabel = data.playerName ?? `Joueur ${data.player ?? '?'}`;
      const cardLabel = data.card != null ? getCardName(data.card) : 'carte inconnue';
      lines.push(`**[Pli ${trickNum}] ${playerLabel} joue ${cardLabel}**`);

      // Layer 1 — Certain (symbolic) deductions: 100% reliable, displayed first
      if (data.certainDeductions?.length) {
        lines.push('**Déductions certaines :**');
        for (const d of data.certainDeductions.slice(0, 4)) {
          lines.push(`  ✓ ${d.description}`);
        }
      }

      // Layer 3 — Conservative ML V1 (probabilistic, displayed below certains)
      if (data.safeDeductions?.length) {
        lines.push('**Déductions prudentes :**');
        for (const d of data.safeDeductions.slice(0, 3)) {
          const label = d.confidenceLabel ?? d.confidence_label ?? 'probablement';
          const labelText = label === 'surement' ? 'sûrement' : label;
          lines.push(`  ▸ ${d.description} (${labelText})`);
        }
      }

      // Differential format (new_insights, reversed, reinforced) — fallback only
      if (lines.length === 1 && (data.newInsights?.length || data.reversed?.length || data.reinforced?.length)) {
        if (data.newInsights?.length) {
          lines.push('**Nouvelles déductions :**');
          for (const d of data.newInsights.slice(0, 5)) {
            const arrow = d.delta > 0 ? '↑' : '↓';
            lines.push(`  ${arrow} ${d.description} (${Math.round(d.prob_before * 100)}% → **${Math.round(d.prob_after * 100)}%**)`);
          }
        }
        if (data.reversed?.length) {
          lines.push('**Renversements :**');
          for (const d of data.reversed.slice(0, 3)) {
            lines.push(`  ⟲ ${d.description} (${Math.round(d.prob_before * 100)}% → **${Math.round(d.prob_after * 100)}%**)`);
          }
        }
        if (data.reinforced?.length) {
          const significant = data.reinforced.filter((d: any) => Math.abs(d.delta) >= 0.2);
          if (significant.length) {
            lines.push('**Confirmations :**');
            for (const d of significant.slice(0, 3)) {
              const arrow = d.delta > 0 ? '↑' : '↓';
              lines.push(`  ${arrow} ${d.description} (${Math.round(d.prob_before * 100)}% → **${Math.round(d.prob_after * 100)}%**)`);
            }
          }
        }
      }

      // Fallback: classic high/low confidence format
      if (lines.length === 1 && data.highConfidence?.length) {
        const descriptions = data.highConfidence
          .slice(0, 5)
          .map((d: any) => `${d.description} (${Math.round(d.probability * 100)}%)`);
        lines.push(...descriptions.map((d: string) => `◇ ${d}`));
      }

      if (lines.length <= 1) return;

      onAddMessage({
        type: 'deduction_update',
        content: lines.join('\n'),
        certainDeductions: data.certainDeductions,
        safeDeductions: data.safeDeductions,
        mlDeductions: data.safeDeductions ?? data.highConfidence ?? data.newInsights,
      });
    };

    socket.on('training:coach_response', handler);
    socket.on('training:deduction_update', deductionHandler);
    return () => {
      socket.off('training:coach_response', handler);
      socket.off('training:deduction_update', deductionHandler);
    };
  }, [socket, onAddMessage]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleAskAdvice = useCallback(() => {
    if (!socket || loading) return;
    setLoading(true);
    onAddMessage({ type: 'user', content: 'Quelle carte me conseilles-tu ?' });
    socket.emit('training:coach_advice', { seat: currentSeat });
  }, [socket, loading, currentSeat, onAddMessage]);

  const handleSendChat = useCallback(() => {
    if (!socket || !input.trim() || loading) return;
    const text = input.trim();
    setInput('');
    setLoading(true);
    onAddMessage({ type: 'user', content: text });
    socket.emit('training:coach_chat', { message: text });
  }, [socket, input, loading, onAddMessage]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendChat();
    }
  }, [handleSendChat]);

  const handleAlternativeClick = useCallback((alt: { card: number; cardName: string }, recommended: string) => {
    if (!socket || loading) return;
    const text = `Pourquoi pourrais-je jouer ${alt.cardName} au lieu de ${recommended} ?`;
    setInput('');
    setLoading(true);
    onAddMessage({ type: 'user', content: text });
    socket.emit('training:coach_chat', { message: text });
  }, [socket, loading, onAddMessage]);

  const hasUserMessage = messages.some(m => m.type === 'user');

  return (
    <div className="coach-chat">
      <div className="coach-chat-messages" ref={scrollRef}>
        {!hasUserMessage && (
          <div className="coach-welcome-message">
            <Markdown>
              {`Bienvenue ! Je suis votre **professeur de tarot**. Je peux vous conseiller sur la carte à jouer, répondre à vos questions, et expliquer les coups joués.\n\n*Clic droit sur une carte jouée pour demander une explication.*`}
            </Markdown>
          </div>
        )}
        {processedMessages.map(msg => (
          <div key={msg.id} className={`coach-message ${msg.type === 'user' ? 'from-user' : msg.type === 'advice' ? 'advice' : msg.type === 'deduction_update' ? 'deduction-update' : 'from-coach'}`}>
            {msg.type === 'advice' ? (
              <AdviceMessage
                msg={msg}
                detailed={adviceDetailed}
                debug={debugML}
                onAlternativeClick={handleAlternativeClick}
              />
            ) : msg.type === 'deduction_update' ? (
              <div className="coach-deduction-update">
                <div className="coach-deduction-update-content">
                  <Markdown>{msg.content}</Markdown>
                </div>
              </div>
            ) : (
              <div className="coach-message-content">
                {msg.type === 'user'
                  ? msg.content
                  : <Markdown>{msg.content}</Markdown>
                }
              </div>
            )}
          </div>
        ))}
        {loading && <div className="coach-loading">Le professeur réfléchit...</div>}
      </div>

      {isPlaying && (
        <button
          className="training-btn training-btn-coach"
          onClick={handleAskAdvice}
          disabled={loading || canAskAdvice === false}
          style={{ margin: '0 8px 4px' }}
        >
          Conseil du professeur
        </button>
      )}

      <div className="coach-chat-input">
        <input
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Poser une question au professeur..."
          disabled={loading}
        />
        <button onClick={handleSendChat} disabled={!input.trim() || loading}>
          Envoyer
        </button>
      </div>
    </div>
  );
}

/* ============================================
 * Advice message sub-component
 * ============================================ */

function AdviceMessage({ msg, detailed, debug, onAlternativeClick }: {
  msg: CoachMessage;
  detailed: boolean;
  debug: boolean;
  onAlternativeClick: (alt: { card: number; cardName: string }, recommended: string) => void;
}) {
  return (
    <>
      {/* Header: card name + confidence + entropy */}
      <div className="coach-advice-header">
        {msg.cardName && (
          <span className="coach-card-name">{msg.cardName}</span>
        )}
        {msg.confidence != null && msg.confidence > 0 && (
          <span className="coach-confidence">({Math.round(msg.confidence * 100)}%)</span>
        )}
        {debug && msg.entropy != null && (
          <span className="coach-entropy" title={`Entropie: ${msg.entropy.toFixed(2)}`}>
            <EntropyBar entropy={msg.entropy} />
          </span>
        )}
      </div>

      {/* Main explanation */}
      <div className="coach-message-content">
        <Markdown>{msg.content}</Markdown>
      </div>

      {/* Layer 1 — Symbolic certain deductions (100%, displayed first) */}
      {msg.certainDeductions && msg.certainDeductions.length > 0 && (
        detailed ? (
          <div className="coach-deductions-block">
            <div className="coach-block-label">Déductions certaines</div>
            {msg.certainDeductions.slice(0, 8).map((d, i) => (
              <div key={i} className="coach-deduction-item" title={d.id}>✓ {d.description}</div>
            ))}
          </div>
        ) : (
          <HoverPanel
            icon="✓"
            label="Déductions certaines"
            count={msg.certainDeductions.length}
            items={msg.certainDeductions.slice(0, 8).map(d => d.description)}
          />
        )
      )}

      {/* Layer 2 — Empirical statistical priors */}
      {msg.statisticalPriors && msg.statisticalPriors.length > 0 && (
        detailed ? (
          <div className="coach-stats-block">
            <div className="coach-block-label">Tendances statistiques</div>
            {msg.statisticalPriors.slice(0, 4).map((m, i) => (
              <div
                key={i}
                className="coach-stat-item"
                title={`n=${m.sampleSize} parties — ${m.id}`}
              >
                ▤ {m.description}
              </div>
            ))}
          </div>
        ) : (
          <HoverPanel
            icon="▤"
            label="Tendances statistiques"
            count={msg.statisticalPriors.length}
            items={msg.statisticalPriors.slice(0, 4).map(m => m.description)}
          />
        )
      )}

      {/* Signals */}
      {msg.signals && msg.signals.length > 0 && (
        detailed ? (
          <div className="coach-signals-block">
            <div className="coach-block-label">Signaux</div>
            {msg.signals.map((s, i) => (
              <div key={i} className="coach-signal-item">◆ {s}</div>
            ))}
          </div>
        ) : (
          <HoverPanel icon="◆" label="Signaux" count={msg.signals.length} items={msg.signals} />
        )
      )}

      {/* Legacy: textual deductions string array (kept for backwards compat) */}
      {msg.deductions && msg.deductions.length > 0 && (
        detailed ? (
          <div className="coach-deductions-block">
            <div className="coach-block-label">Faits observés</div>
            {msg.deductions.map((d, i) => (
              <div key={i} className="coach-deduction-item">▸ {d}</div>
            ))}
          </div>
        ) : (
          <HoverPanel icon="▸" label="Faits observés" count={msg.deductions.length} items={msg.deductions} />
        )
      )}

      {/* Safe ML deductions (conservative V1) */}
      {msg.safeDeductions && msg.safeDeductions.length > 0 && (
        detailed ? (
          <div className="coach-ml-deductions-block">
            <div className="coach-block-label">Déductions prudentes</div>
            {msg.safeDeductions.slice(0, 8).map((d, i) => {
              const label = d.confidenceLabel ?? d.confidence_label ?? 'probablement';
              const labelText = label === 'surement' ? 'sûrement' : label;
              return (
                <div key={i} className="coach-ml-deduction-item" title={`${d.category} — ${d.id}`}>
                  <span className="coach-ml-icon">▸</span>
                  <span className="coach-ml-desc">{d.description}</span>
                  <span className={`coach-ml-prob ${d.probability >= 0.97 ? 'high' : d.probability >= 0.93 ? 'medium' : 'low'}`}>
                    {labelText}
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <HoverPanel
            icon="▸"
            label="Déductions prudentes"
            count={msg.safeDeductions.length}
            items={msg.safeDeductions.slice(0, 8).map(d => {
              const label = d.confidenceLabel ?? d.confidence_label ?? 'probablement';
              const labelText = label === 'surement' ? 'sûrement' : label;
              return `${d.description} (${labelText})`;
            })}
          />
        )
      )}

      {/* ML deductions (speculative) */}
      {msg.mlDeductions && msg.mlDeductions.length > 0 && (
        detailed ? (
          <div className="coach-ml-deductions-block">
            <div className="coach-block-label">Estimations ML</div>
            {msg.mlDeductions.slice(0, 10).map((d, i) => (
              <div key={i} className="coach-ml-deduction-item" title={`${d.category} — ${d.id}`}>
                <span className="coach-ml-icon">◇</span>
                <span className="coach-ml-desc">{d.description}</span>
                <span className={`coach-ml-prob ${d.probability >= 0.9 ? 'high' : d.probability >= 0.7 ? 'medium' : 'low'}`}>
                  {Math.round(d.probability * 100)}%
                </span>
              </div>
            ))}
          </div>
        ) : (
          <HoverPanel
            icon="◇"
            label="Estimations ML"
            count={msg.mlDeductions.length}
            items={msg.mlDeductions.slice(0, 10).map(d =>
              `${d.description} (${Math.round(d.probability * 100)}%)`
            )}
          />
        )
      )}

      {/* Alternatives (clickable) */}
      {msg.alternatives && msg.alternatives.length > 0 && (
        <div className="coach-alternatives">
          <span className="coach-alternatives-label">Alternatives :</span>
          {msg.alternatives.map((a, i) => (
            <button
              key={i}
              className="coach-alternative-btn"
              onClick={() => onAlternativeClick(a, msg.cardName ?? '?')}
              title={`Demander pourquoi jouer ${a.cardName}`}
            >
              {a.cardName} ({Math.round(a.confidence * 100)}%)
            </button>
          ))}
        </div>
      )}
    </>
  );
}

/* ============================================
 * Entropy bar (debug display)
 * ============================================ */

function EntropyBar({ entropy }: { entropy: number }) {
  const maxEntropy = 4.0;
  const pct = Math.min(100, (entropy / maxEntropy) * 100);
  const label = entropy < 0.5 ? 'confiant' : entropy < 1.5 ? 'modéré' : entropy < 2.5 ? 'incertain' : 'très incertain';
  return (
    <span className="coach-entropy-bar" title={`Entropie: ${entropy.toFixed(2)} (${label})`}>
      <span className="coach-entropy-fill" style={{ width: `${pct}%` }} />
      <span className="coach-entropy-label">{entropy.toFixed(1)}</span>
    </span>
  );
}

/* ============================================
 * Hover panel for compact signals/deductions
 * ============================================ */

function HoverPanel({ icon, label, count, items }: {
  icon: string; label: string; count: number; items: string[];
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [flipUp, setFlipUp] = useState(false);

  useEffect(() => {
    if (!open || !panelRef.current || !containerRef.current) return;
    const panelRect = panelRef.current.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();
    if (panelRect.bottom > window.innerHeight - 10) {
      setFlipUp(true);
    } else if (containerRect.top - panelRect.height > 10) {
      setFlipUp(false);
    }
  }, [open]);

  return (
    <div
      className="coach-hover-container"
      ref={containerRef}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => { setOpen(false); setFlipUp(false); }}
    >
      <span className="coach-hover-trigger">
        <span className="coach-hover-icon">{icon}</span>
        <span className="coach-hover-label">{label} ({count})</span>
      </span>
      {open && (
        <div ref={panelRef} className={`coach-hover-panel ${flipUp ? 'flip-up' : ''}`}>
          {items.map((item, i) => (
            <div key={i} className="coach-hover-panel-item">{icon} {item}</div>
          ))}
        </div>
      )}
    </div>
  );
}
